import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  tagCategorySchema,
  useUpdateTagCategory,
  TagCategoryFormValues,
} from "@/api/tags";
import { useEffect } from "react";
import { TagCategory } from "@/api.types";

export function EditTagCategoryForm({
  category,
  open,
  onOpenChange,
}: {
  category: TagCategory;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const form = useForm<TagCategoryFormValues>({
    resolver: zodResolver(tagCategorySchema),
    defaultValues: {
      name: "",
      description: "",
      isActive: true,
    },
  });

  const updateTagCategoryMutation = useUpdateTagCategory(category.id, {
    onSuccess: () => {
      onOpenChange(false);
    },
  });

  useEffect(() => {
    if (category) {
      form.reset({
        name: category.name,
        description: category.description ?? "",
        isActive: category.isActive,
      });
    }
  }, [category, form]);

  const onSubmit = (data: TagCategoryFormValues) => {
    if (category.id) {
      updateTagCategoryMutation.mutate(data);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Category</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Category name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Category description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full"
              disabled={updateTagCategoryMutation.isPending}
            >
              {updateTagCategoryMutation.isPending
                ? "Updating..."
                : "Update Category"}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
